import { useEffect, useRef } from 'react';
import type { ExternalFilters } from '../types/ExternalFilters';
import type { Filters } from '../types/Filters';
import { unwrapFilterValuesRaw } from '../utils/unwrapFilterValuesRaw';
type UseGridFilterSyncParams = {
  filterValues: Filters;
  onExternalFilterChange: ((filters: ExternalFilters) => void) | undefined;
};

/**
 * Notifies the host whenever filterValues change, EXCLUDING the initial mount.
 *
 * Uses a prevRef pattern: skip when null (first run) or when reference is unchanged.
 * store.setFilterValues has a referential equality guard, so this fires only on actual mutations.
 */
export const useGridFilterSync = (params: UseGridFilterSyncParams): void => {
  const onExternalFilterChangeRef = useRef(params.onExternalFilterChange);

  // eslint-disable-next-line no-restricted-syntax -- intentional: no-deps useEffect syncs ref every render
  useEffect(() => {
    onExternalFilterChangeRef.current = params.onExternalFilterChange;
  });
  const prevFilterValuesForNotificationRef = useRef<typeof params.filterValues | null>(null);

  // eslint-disable-next-line no-restricted-syntax -- intentional: reactive notification with prev-ref skip-on-mount guard; notifies host on filter changes from any source (preset switch, URL hydration, user filter action)
  useEffect(() => {
    // Skip initial mount — the host doesn't need a notification for the seed values.
    if (prevFilterValuesForNotificationRef.current === null) {
      prevFilterValuesForNotificationRef.current = params.filterValues;
      return;
    }
    // Referential equality guard — store.setFilterValues skips same-ref updates,
    // so this will only fire on actual filter mutations.
    if (prevFilterValuesForNotificationRef.current === params.filterValues) {
      return;
    }
    prevFilterValuesForNotificationRef.current = params.filterValues;
    onExternalFilterChangeRef.current?.(unwrapFilterValuesRaw(params.filterValues));
  }, [params.filterValues]);
};